import { DestroyRef, Injectable, NgZone, Signal, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent } from 'rxjs';
import { FilingLoaderStrategy } from './filing-loader.strategy';
import { FILING_FORM_ROUTE } from '../../../utils/field-link-navigation.util';

/**
 * Full-page blocking-overlay strategy for the filing-form container.
 *
 * Covers the entire transition from "user navigates to `/filing`" up to
 * "all sheets hydrated and ready". Paired with `FilingLoadOverlayComponent`
 * via the loader registry — the UI renders a blocking spinner with live
 * phase / sheet / timer information, followed by a completion popup.
 *
 * Lifecycle:
 * 1. `begin()` is auto-invoked when the Router emits `NavigationStart`
 *    toward `/filing` — this covers the guard + component-mount window
 *    without requiring every click-source to call in.
 * 2. The container reports progress via `reportPhase(...)` and
 *    `reportSheet(...)` as hydration advances.
 * 3. `end()` is invoked from the container's `finalize()` block so it
 *    always fires on success, error, and unsubscribe paths. On `end()`
 *    the final elapsed duration is snapshotted into
 *    {@link lastCompletedElapsedMs} so the overlay can render a
 *    completion popup; consumers dismiss it via
 *    {@link acknowledgeCompletion}.
 *
 * This is a proof-of-concept for the product team — keep the surface
 * minimal and prefer signals over streamed observables so the overlay
 * template can bind directly.
 *
 * @see docs/ui/loaders/filing/README.md
 */
@Injectable()
export class BlockingOverlayFilingLoaderStrategy extends FilingLoaderStrategy {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  private readonly isActiveSignal = signal<boolean>(false);
  private readonly phaseLabelSignal = signal<string>('');
  private readonly currentSheetLabelSignal = signal<string | null>(null);
  private readonly completedCountSignal = signal<number>(0);
  private readonly totalCountSignal = signal<number>(0);
  private readonly elapsedMsSignal = signal<number>(0);
  private readonly lastCompletedElapsedMsSignal = signal<number | null>(null);

  readonly isActive: Signal<boolean> = this.isActiveSignal.asReadonly();
  readonly phaseLabel: Signal<string> = this.phaseLabelSignal.asReadonly();
  readonly currentSheetLabel: Signal<string | null> = this.currentSheetLabelSignal.asReadonly();
  readonly completedCount: Signal<number> = this.completedCountSignal.asReadonly();
  readonly totalCount: Signal<number> = this.totalCountSignal.asReadonly();
  readonly elapsedMs: Signal<number> = this.elapsedMsSignal.asReadonly();
  readonly lastCompletedElapsedMs: Signal<number | null> = this.lastCompletedElapsedMsSignal.asReadonly();

  private startedAt = 0;
  private rafHandle: number | null = null;

  constructor() {
    super();

    // Auto-begin on any navigation that targets a filing route. This
    // covers the route-guard phase — by the time the container mounts
    // the overlay is already showing.
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (this.isFilingNavigation(event.url) && !this.isActive()) {
          this.begin('Verifying access...');
        }
      });

    // Browsers throttle (or outright freeze) `setInterval` in background
    // tabs — which manifested as the elapsed timer jumping in 1–5 s
    // chunks while the user was Alt-Tabbed away. `requestAnimationFrame`
    // is explicitly paused while the tab is hidden and resumed at the
    // monitor's native refresh rate on return, so there's nothing left
    // to throttle. The visibility listener below force-syncs elapsedMs
    // the moment the tab regains focus (before the next rAF fires).
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (document.visibilityState === 'visible' && this.isActive()) {
            this.elapsedMsSignal.set(Date.now() - this.startedAt);
          }
        });
    }

    // Ensure the rAF loop is torn down if the strategy itself is destroyed
    // (e.g. hot-reload in dev) — otherwise it would keep scheduling frames.
    this.destroyRef.onDestroy(() => this.stopTimer());
  }

  begin(initialPhase: string = 'Preparing filing workspace...'): void {
    this.stopTimer();

    this.startedAt = Date.now();
    this.elapsedMsSignal.set(0);
    this.lastCompletedElapsedMsSignal.set(null);
    this.phaseLabelSignal.set(initialPhase);
    this.currentSheetLabelSignal.set(null);
    this.completedCountSignal.set(0);
    this.totalCountSignal.set(0);
    this.isActiveSignal.set(true);

    this.startTimer();
  }

  reportPhase(label: string): void {
    if (!this.isActive()) return;
    this.phaseLabelSignal.set(label);
    this.currentSheetLabelSignal.set(null);
  }

  reportSheet(sheetLabel: string, completed: number, total: number): void {
    if (!this.isActive()) return;
    this.phaseLabelSignal.set('Validating sheets');
    this.currentSheetLabelSignal.set(sheetLabel);
    this.completedCountSignal.set(completed);
    this.totalCountSignal.set(total);
  }

  end(): void {
    if (!this.isActive()) return;
    this.stopTimer();
    this.lastCompletedElapsedMsSignal.set(Date.now() - this.startedAt);
    this.isActiveSignal.set(false);
    this.phaseLabelSignal.set('');
    this.currentSheetLabelSignal.set(null);
    this.completedCountSignal.set(0);
    this.totalCountSignal.set(0);
    this.elapsedMsSignal.set(0);
  }

  acknowledgeCompletion(): void {
    this.lastCompletedElapsedMsSignal.set(null);
  }

  private isFilingNavigation(url: string): boolean {
    // Match the filing route (`/mbrs/filing`), with a query or a child path,
    // but reject lookalike prefixes such as `/mbrs/filing-something-else`.
    // Keyed off FILING_FORM_ROUTE: this used to hard-code `/filing`, a path
    // the app never mounts, so the auto-begin could never fire.
    const filingPath = FILING_FORM_ROUTE[0];
    const path = url.split(/[?#]/)[0];
    return path === filingPath || path.startsWith(`${filingPath}/`);
  }

  /**
   * Start the rAF-driven timer loop. Scheduled OUTSIDE the Angular zone
   * to avoid kicking off a change-detection pass on every animation
   * frame (60+ per second) during an already CPU-intensive validation
   * cycle. The signal write inside still triggers component-level
   * updates via Angular's signal-based reactivity.
   */
  private startTimer(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const tick = () => {
        if (!this.isActive()) {
          this.rafHandle = null;
          return;
        }
        this.elapsedMsSignal.set(Date.now() - this.startedAt);
        this.rafHandle = requestAnimationFrame(tick);
      };
      this.rafHandle = requestAnimationFrame(tick);
    });
  }

  private stopTimer(): void {
    if (this.rafHandle !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.rafHandle);
    }
    this.rafHandle = null;
  }
}
